export const getPbBarStyle = (color) => {
  if (!color) return {};
  const raw = String(color).trim();
  if (raw.startsWith('var(')) {
    return {
      background: raw,
      border: 'none',
      boxShadow: '0 1px 2px var(--border)',
      borderRadius: '4px',
    };
  }

  let c = raw.toUpperCase();
  if (!c.startsWith('#')) {
    c = `#${c}`;
  }
  if (c.length > 7) {
    c = c.substring(0, 7);
  }

  return {
    background: c,
    border: 'none',
    boxShadow: '0 1px 2px var(--border)',
    borderRadius: '4px',
  };
};

/**
 * CANONICAL GRADIENT SPECTRUM
 * Exactly extracted 30-step sequential gradient from source design specification.
 * Provides guaranteed perfect visual harmony and unlimited aesthetic continuity.
 */
export const CANONICAL_SPECTRUM = [
  // User-Authorized Elite Active Palette
  '#276ED8', '#4791E8', '#71B4F5', '#A0D5FF', 
  '#8B7DF8', '#9C88FA', '#BBA7FD', '#C9B5FF', 
  '#F5933C', '#1C866A', '#A3D7C4', '#EBF212',
  '#B5DBFF', '#8EC0F7', '#6AA5EE', '#488AE4',
  '#85C2AD', '#66AE96', '#459A80'
];

export const getCompanyColor = (companyName) => {
  if (!companyName) return '#FFFFFF';
  const c = companyName.toString().toLowerCase().trim();
  
  // --- CANONICAL BRAND TERRACOTTA (Updated to use Master Spectrum for Unified Charts) ---
  if (c.includes('lsw') || c.includes('national life') || c.includes('subject')) {
    return CANONICAL_SPECTRUM[0]; // Master Anchor: Row 3 Anchor 1
  }

  // --- HARMONIOUS VIP MAPPING (Sampled linearly to distribute evenly across full cycle) ---
  if (c.includes('pacific life')) return CANONICAL_SPECTRUM[1];    // Row 4
  if (c.includes('nationwide life')) return CANONICAL_SPECTRUM[2]; // Row 2
  if (c.includes('nationwide ann')) return CANONICAL_SPECTRUM[3];  // Row 5
  if (c.includes('john hancock')) return CANONICAL_SPECTRUM[4];    // Row 1
  if (c.includes('jackson national')) return CANONICAL_SPECTRUM[5]; // Row 3 - Cycle 2
  if (c.includes('mutual of omaha')) return CANONICAL_SPECTRUM[6];  // Row 4 - Cycle 2
  
  let hash = 0;
  for (let i = 0; i < c.length; i++) {
    hash = ((hash << 5) - hash) + c.charCodeAt(i);
    hash |= 0; 
  }
  
  // Inject high-dispersion prime multiplication to blow up spatial grouping and guarantee maximum contrast
  return CANONICAL_SPECTRUM[(Math.abs(hash) * 137) % CANONICAL_SPECTRUM.length];
};

/**
 * Constrains spectrum pool strictly to the harmonious Cool side (Greens, Teals, Blues, Purples).
 * Perfectly satisfied 'blues and greens and purples' constraint dynamically for any component.
 */
export const getCompanyCoolColor = (companyName) => {
  if (!companyName) return '#FFFFFF';
  const c = companyName.toString().toLowerCase().trim();

  // --- UNIFIED BRAND HERO (Subject Anchor) ---
  if (c.includes('lsw') || c.includes('national life') || c.includes('subject')) {
    return CANONICAL_SPECTRUM[0]; 
  }

  // The current Master Spectrum already conforms strictly to the Lime -> Blue -> Purple boundary suite.
  const COOL_POOL = CANONICAL_SPECTRUM; 

  let hash = 0;
  for (let i = 0; i < c.length; i++) {
    hash = ((hash << 5) - hash) + c.charCodeAt(i);
    hash |= 0; 
  }
  
  // Inject high-dispersion prime multiplication to blow up spatial grouping and guarantee maximum contrast
  return COOL_POOL[(Math.abs(hash) * 137) % COOL_POOL.length];
};

/** Filing volume chart dataset colors (tokens.css). */
export const FILING_VOLUME_TYPE_COLORS = {
  default: 'var(--c-blue-4)',
  advertising: 'var(--c-blue-1)',
  form: 'var(--c-blue-2)',
  rate: 'var(--c-blue-3)',
};

export const getFilingVolumeTypeColor = (label) => {
  const key = (label || '').toLowerCase();
  if (key.includes('advertising')) return FILING_VOLUME_TYPE_COLORS.advertising;
  if (key.includes('form')) return FILING_VOLUME_TYPE_COLORS.form;
  if (key.includes('rate')) return FILING_VOLUME_TYPE_COLORS.rate;
  return FILING_VOLUME_TYPE_COLORS.default;
};

export const COMPETITOR_CADENCE_COLOR = 'var(--c-purple-1)';

/** Lavender intensity ramp — Filings cadence heatmap & SEC activity heatmap (low → high). */
export const CADENCE_HEATMAP_RAMP = [
  '#c9b5ff',
  '#bba7fd',
  '#ac98fb',
  '#9c8bfa',
  '#8b7df8',
];

export const parseWeekLabelNumber = (label) => {
  const m = String(label || '').match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 0;
};

export const sortWeekLabelsChronologically = (weeks = []) =>
  [...weeks].sort((a, b) => parseWeekLabelNumber(a) - parseWeekLabelNumber(b));

/** Align cadence row values with chronologically sorted week columns. */
export const sortCompetitorCadenceByWeeks = (weeks = [], rows = []) => {
  const sortedWeeks = sortWeekLabelsChronologically(weeks);
  if (!sortedWeeks.length) return { weeks: sortedWeeks, rows };
  const indexByWeek = new Map(weeks.map((wk, i) => [wk, i]));
  const sortedRows = rows.map((row) => ({
    ...row,
    vals: sortedWeeks.map((wk) => row.vals?.[indexByWeek.get(wk)] ?? 0),
  }));
  return { weeks: sortedWeeks, rows: sortedRows };
};
